import { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useScrollLock } from 'usehooks-ts';

import { Breakpoint, useScreenSize } from '@/lib/hooks/use-screen-size';
import { useSessionSetting } from '@/lib/hooks/use-session-setting';

import { LayoutContext } from './layout-context';

export function LayoutContextProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const screenSize = useScreenSize();
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useSessionSetting<boolean | null>(
    'sidebar.left.open',
    null,
  );
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useSessionSetting<boolean>(
    'sidebar.right.open',
    // Default to open only on desktop
    screenSize.breakpoint > Breakpoint.md,
  );
  const [activeExplorationSourceId, setActiveExplorationSourceId] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const isLeftSidebarOpenByDefault = screenSize.breakpoint > Breakpoint.lg;

  // TODO: Add keyboard shortcuts to toggle sidebar
  const toggleLeftSidebar = (newVal?: boolean) =>
    setIsLeftSidebarOpen(newVal ?? !(isLeftSidebarOpen ?? isLeftSidebarOpenByDefault));
  const toggleRightSidebar = (newVal?: boolean) =>
    setIsRightSidebarOpen(newVal ?? !isRightSidebarOpen);

  // Scroll lock for mobile
  const { lock, unlock, isLocked } = useScrollLock({
    autoLock: false,
  });
  useEffect(() => {
    if (screenSize.breakpoint <= Breakpoint.lg) {
      isLeftSidebarOpen === true ? lock() : unlock();
    } else if (screenSize.breakpoint > Breakpoint.lg && isLocked) {
      unlock();
    }
  }, [isLeftSidebarOpen, lock, unlock, isLocked, screenSize.breakpoint]);

  useEffect(() => {
    if (!location.pathname.includes('/explore/')) {
      setActiveExplorationSourceId(null);
    }
  }, [location.pathname]);

  const embeddedModeParam = searchParams.get('embed');
  useEffect(() => {
    if (embeddedModeParam === 'true') {
      sessionStorage.setItem('embed', 'true');
    }
  }, [embeddedModeParam]);
  const isEmbeddedMode = embeddedModeParam === 'true' || sessionStorage.getItem('embed') === 'true';

  const scrollToTop = () => {
    scrollAreaRef.current?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  };

  return (
    <LayoutContext.Provider
      value={{
        isLeftSidebarOpen: isLeftSidebarOpen ?? isLeftSidebarOpenByDefault,
        setIsLeftSidebarOpen,
        toggleLeftSidebar,
        isRightSidebarOpen,
        setIsRightSidebarOpen,
        toggleRightSidebar,
        activeExplorationSourceId,
        setActiveExplorationSourceId,
        isEmbeddedMode,
        scrollAreaRef,
        scrollToTop,
      }}>
      {children}
    </LayoutContext.Provider>
  );
}
